import classNames from 'classnames';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Accept, DropzoneInputProps, DropzoneRootProps } from 'react-dropzone';
import { CropArea, CropModal } from 'ud-form/components/image-crop';
import { useImageGallery } from 'ud-ui/components/image-gallery/modal';
import { DropzonePreview } from '../domain/previews';
import { DropzonePreviewPlacement } from '../types';
import { DropzoneBody, DropzoneBodyProps } from './dropzone-body';
import { DropzoneHint } from './hint';
import { PreviewList } from './preview-list';
import { RemoveConfirmModal } from './remove-confirm-modal';
import { Field, Zone } from './styles';

export interface UDDropzoneProps extends DropzoneBodyProps {
  previews: DropzonePreview[];
  rootProps: DropzoneRootProps;
  inputProps: DropzoneInputProps;

  accept: Accept;
  maxSize: number;

  preview?: DropzonePreviewPlacement;
  previewSize?: number;
  showFileName?: boolean;
  sortable?: boolean;
  removable?: boolean;
  gallery?: boolean;

  isDragActive?: boolean;
  isFocused?: boolean;
  isDisabled?: boolean;
  hasError?: boolean;

  onRemove?: (index: number) => void;
  onReorder?: (from: number, to: number) => void;

  /**
   * Файл, который сейчас кадрируют. `null` — модалки нет.
   *
   * Открытостью модалки управляет файл, а не флаг: так состояние кропа не может
   * разойтись с тем, что показано, — см. `CropModalProps`.
   */
  cropFile?: File | null;
  /** Соотношение сторон рамки кадрирования. По умолчанию свободное. */
  cropAspect?: number;
  onCrop?: (area: CropArea) => void;
  onCropSkip?: () => void;
  onCropCancel?: () => void;

  /**
   * Что показать под подсказкой вместо превью.
   *
   * Нужно там, где картинку рисует само поле, а не дропзона: логотип
   * организатора показывается на выбранной подложке и с переходом к смене цвета.
   */
  children?: ReactNode;

  className?: string;
}

/** Миниатюра внутри зоны меньше: она делит строку с приглашением выбрать файл. */
const SIZE_INSIDE = 60;
const SIZE_BELOW = 100;

/**
 * Дропзона без Formik: получает готовые превью и обработчики, рисует зону,
 * подсказку и выбранные картинки.
 *
 * Логика поля живёт в `useDropzoneField` — здесь только показ, как и у остальных
 * полей кита, где `UDInput` ничего не знает про форму.
 */
export function UDDropzone(props: UDDropzoneProps) {
  const {
    previews,
    rootProps,
    inputProps,
    accept,
    maxSize,
    preview = 'below',
    previewSize,
    showFileName,
    sortable,
    removable,
    gallery,
    isDragActive,
    isFocused,
    isProcessing,
    isDisabled,
    hasError,
    onRemove,
    onReorder,
    cropFile,
    cropAspect,
    onCrop,
    onCropSkip,
    onCropCancel,
    children,
    className,
    ...bodyProps
  } = props;

  const [indexToRemove, setIndexToRemove] = useState<number | null>(null);

  const { handleImageClick, GalleryModalWrapper, setImages } = useImageGallery();
  const galleryImages = useMemo(() => previews.map(({ url, name }) => ({ url, name })), [previews]);

  useEffect(() => {
    setImages(galleryImages);
  }, [galleryImages, setImages]);

  const size = previewSize ?? (preview === 'inside' ? SIZE_INSIDE : SIZE_BELOW);

  const previewList = preview !== 'none' && (
    <PreviewList
      previews={previews}
      size={size}
      showFileName={showFileName}
      sortable={sortable}
      onRemove={removable ? setIndexToRemove : undefined}
      onReorder={onReorder}
      // Лайтбокс только под зоной: внутри неё клик по картинке открывает выбор
      // файла — вся плитка задумана как одна большая кнопка.
      onOpen={gallery && preview === 'below' ? handleImageClick : undefined}
    />
  );

  return (
    <Field className={className}>
      <Zone
        {...rootProps}
        className={classNames('dropzone', rootProps.className, {
          active: isDragActive,
          focused: isFocused,
          error: hasError,
          disabled: isDisabled,
        })}
      >
        <input {...inputProps} aria-label="Выберите изображение" />
        {preview === 'inside' && previewList}
        <DropzoneBody {...bodyProps} isProcessing={isProcessing} />
      </Zone>

      <DropzoneHint accept={accept} maxSize={maxSize} />

      {preview === 'below' && previewList}
      {children}

      <CropModal
        file={cropFile ?? null}
        aspect={cropAspect}
        onCrop={(area) => onCrop?.(area)}
        onSkip={() => onCropSkip?.()}
        onCancel={() => onCropCancel?.()}
      />

      <RemoveConfirmModal
        isOpen={indexToRemove !== null}
        onCancel={() => setIndexToRemove(null)}
        onConfirm={() => {
          if (indexToRemove !== null) onRemove?.(indexToRemove);
          setIndexToRemove(null);
        }}
      />
      <GalleryModalWrapper />
    </Field>
  );
}
